import React from 'react';
import { motion } from 'framer-motion';
import { Building2, CalendarClock, MessageSquare, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const VALUE_POINTS = [
  {
    title: 'Informasi yang konsisten',
    body: 'Harga, stok, ongkir, dan kebijakan toko dijawab dari satu sumber data yang sama. Jawabannya seragam di semua kanal dan semua shift.',
  },
  {
    title: 'Kapasitas pelayanan bertambah',
    body: 'Pertanyaan yang berulang dijawab otomatis. Tim Anda jadi punya waktu untuk percakapan yang memang butuh keputusan manusia.',
  },
  {
    title: 'Pengalaman pelanggan yang mulus',
    body: 'Riwayat chat tiap pelanggan tersimpan lintas kanal. Pembeli tidak perlu mengulang ceritanya dari awal.',
  },
];

const DEMO_MAILTO =
  'mailto:sales@nerava.id?subject=Permintaan%20Jadwal%20Demo%20Nerava&body=Halo%20tim%20Nerava%2C%0A%0ANama%3A%0APerusahaan%3A%0AJumlah%20agen%2Ftim%20CS%3A%0AKanal%20yang%20dipakai%20(WhatsApp%2FInstagram%2FMessenger)%3A%0AWaktu%20yang%20cocok%20untuk%20demo%3A%0A%0ATerima%20kasih.';

const NEEDS_MAILTO =
  'mailto:sales@nerava.id?subject=Diskusi%20Kebutuhan%20Bisnis%20-%20Nerava&body=Halo%20tim%20Nerava%2C%0A%0ANama%3A%0APerusahaan%3A%0AKebutuhan%20yang%20ingin%20didiskusikan%3A%0APerkiraan%20volume%20chat%20per%20bulan%3A%0AIntegrasi%20internal%20yang%20perlu%20dihubungkan%3A%0A%0ATerima%20kasih.';

export const EnterpriseBand = ({ className = '' }) => (
  <motion.div
    className={className}
    data-testid="enterprise-band"
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
  >
    <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900 sm:p-10">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-indigo-500/10 blur-3xl dark:bg-indigo-500/20"
      />

      <div className="relative grid gap-10 lg:grid-cols-12 lg:gap-12">
        <div className="text-center lg:col-span-5 lg:text-left">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
            <Building2 className="h-3.5 w-3.5" />
            Enterprise
          </span>
          <h2
            className="mt-5 text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl"
            style={{ fontFamily: 'Manrope' }}
          >
            Punya tim CS dan volume chat besar?
          </h2>
          <p className="mx-auto mt-4 max-w-md text-base text-slate-600 dark:text-zinc-300 md:text-lg lg:mx-0 lg:max-w-none">
            Kalau kebutuhan Anda sudah melewati paket Business, kami siapkan penawaran khusus. Jumlah
            agen, integrasi ke sistem internal, dan ketentuan layanan disesuaikan dengan cara kerja
            bisnis Anda.
          </p>

          <div className="mx-auto mt-7 flex max-w-md flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center lg:mx-0 lg:max-w-none lg:flex-col lg:justify-start">
            <a href={DEMO_MAILTO} className="w-full sm:w-auto lg:w-full">
              <Button
                data-testid="enterprise-cta-demo"
                className="group w-full rounded-full bg-indigo-600 px-7 py-6 text-base font-semibold text-white shadow-[0_18px_36px_-18px_rgba(79,70,229,0.7)] transition-transform hover:-translate-y-0.5 hover:bg-indigo-700 sm:w-auto lg:w-full"
              >
                <CalendarClock className="mr-2 h-5 w-5" />
                Jadwalkan Demo
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
            <a href={NEEDS_MAILTO} className="w-full sm:w-auto lg:w-full">
              <Button
                data-testid="enterprise-cta-needs"
                variant="outline"
                className="w-full rounded-full border border-slate-300 bg-transparent px-7 py-6 text-base transition-colors hover:border-indigo-400 hover:text-indigo-700 dark:border-zinc-700 dark:hover:border-indigo-500/70 dark:hover:text-indigo-300 sm:w-auto lg:w-full"
              >
                <MessageSquare className="mr-2 h-5 w-5" />
                Diskusikan Kebutuhan Bisnis
              </Button>
            </a>
          </div>

          <p className="mt-5 text-sm text-slate-500 dark:text-zinc-400">
            Email langsung:{' '}
            <a
              href="mailto:sales@nerava.id"
              className="font-semibold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              sales@nerava.id
            </a>
          </p>
        </div>

        <div className="lg:col-span-7">
          <div className="grid gap-3 sm:gap-4">
            {VALUE_POINTS.map((point, index) => (
              <div
                key={point.title}
                data-testid={`enterprise-point-${index + 1}`}
                className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 text-center transition-colors hover:border-indigo-300 dark:border-zinc-800 dark:bg-zinc-950/40 dark:hover:border-indigo-500/50 lg:text-left"
              >
                <div className="lg:flex lg:gap-3">
                  <span className="font-mono text-xs text-indigo-500 dark:text-indigo-400 lg:mt-1 lg:w-5 lg:flex-shrink-0 lg:text-left">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0">
                    <h3 className="mt-1 text-base font-semibold text-slate-900 dark:text-white lg:mt-0">
                      {point.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-zinc-400">
                      {point.body}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

export default EnterpriseBand;
